import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

interface Producto {
  id: number;
  attributes: {
    precioFinal: number;
    [key: string]: unknown;
  };
}

interface CarritoItem {
  cantidad: number;
  producto: Producto;
}

type InitialData = {
  productos: Producto[];
  totalPages: number;
  totalResults: number;
};

const MINIMO_UNIDADES_MAYORISTA = 20;
const RESULTS_PER_PAGE = 24;

const useHook = (initial: InitialData) => {

  const Router = useRouter();
  const carritoMayorista = useSelector((state: { carritoMayorista: { productos: CarritoItem[] } }) => state.carritoMayorista.productos);

  // filtros
  const [isReady, setIsReady] = useState(false);
  const [tipo, setTipo] = useState<string[]>([]);
  const [tematica, setTematica] = useState<string[]>([]);
  const [color, setColor] = useState<string[]>([]);
  const [destacado, setDestacado] = useState(false);
  const [novedad, setNovedad] = useState(false);

  // productos
  const [productos, setProductos] = useState<Producto[]>(initial.productos);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const fetchAbortRef = useRef<AbortController | null>(null);

  // paginado / infinite scroll
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initial.totalPages);
  const [totalResults, setTotalResults] = useState(initial.totalResults);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const isFirstFilterRun = useRef(true);

  const [showCartButton, setShowCartButton] = useState(false);
  const [totalUnidades, setTotalUnidades] = useState<number | null>(null);
  const [totalPrecio, setTotalPrecio] = useState(0);

  // al cambiar de filtro la grilla puede achicarse mucho y el footer "sube" de golpe;
  // volvemos a la parte de arriba de la grilla para que no quede desorientado
  const scrollToTop = () => {
    if (!topRef.current) return;
    const header = document.querySelector('header');
    const headerHeight = header?.getBoundingClientRect().height || 0;
    const top = topRef.current.getBoundingClientRect().top + window.scrollY - headerHeight - 20;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const handleTipo = (v: string[]) => setTipo(v.map(s => s.trim()).filter(Boolean));
  const handleTematica = (v: string[]) => setTematica(v.map(s => s.trim()).filter(Boolean));
  const handleColor = (v: string[]) => setColor(v.map(s => s.trim()).filter(Boolean));
  const handleDestacado = () => setDestacado(prev => !prev);
  const handleNovedad = () => setNovedad(prev => !prev);

  const handleClearFiltros = () => {
    setTipo([]);
    setTematica([]);
    setColor([]);
    setDestacado(false);
    setNovedad(false);
  };

  const fetchProductos = async (targetPage: number, append: boolean) => {
    fetchAbortRef.current?.abort();
    const abortController = new AbortController();
    fetchAbortRef.current = abortController;

    if (append) setLoadingMore(true);
    else setLoadingProductos(true);

    const body = {
      liviano: true,
      page: targetPage,
      pageSize: RESULTS_PER_PAGE,
      tipo,
      tematica,
      color,
      destacado,
      novedad,
      aptoVentaMayorista: true,
    };

    try {
      const result = await fetch(`${process.env.NEXT_PUBLIC_URL_FRONTEND}/api/productos/get-filtered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: abortController.signal,
      });
      const data = await result.json();
      if (data?.data) {
        setProductos(prev => append ? [...prev, ...data.data] : data.data);
        setTotalPages(Math.ceil(data?.meta?.pagination?.total / RESULTS_PER_PAGE));
        setTotalResults(data?.meta?.pagination?.total);
      }
      setLoadingProductos(false);
      setLoadingMore(false);
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      console.log("e", err);
      setLoadingProductos(false);
      setLoadingMore(false);
    }
  };

  // refs para que el observer del infinite scroll siempre lea el último estado/filtros sin recrearse
  const latestRef = useRef({ page, totalPages, loadingProductos, loadingMore, fetchProductos });
  useEffect(() => {
    latestRef.current = { page, totalPages, loadingProductos, loadingMore, fetchProductos };
  });

  // capta las query de la url, y los convierte en estados
  const getFiltersFromUrl = async () => {
    const { tematica, tipo, color, destacado, novedad } = Router.query;
    setTematica((tematica as string || '').split(',').filter(Boolean));
    setTipo((tipo as string || '').split(',').filter(Boolean));
    setColor((color as string || '').split(',').filter(Boolean));
    setDestacado(destacado === '1');
    setNovedad(novedad === '1');
    setIsReady(true);
  };

  useEffect(() => {
    if (!Router.isReady) return;
    getFiltersFromUrl();
  }, [Router.isReady]);

  // sync filtros -> URL (sin recargar) + refetch desde página 1 en cada cambio de filtro
  useEffect(() => {
    if (!isReady) return;

    const query: Record<string, string> = {};
    if (tipo.length) query.tipo = tipo.join(',');
    if (tematica.length) query.tematica = tematica.join(',');
    if (color.length) query.color = color.join(',');
    if (destacado) query.destacado = '1';
    if (novedad) query.novedad = '1';

    Router.replace({ pathname: Router.pathname, query }, undefined, { shallow: true });

    if (isFirstFilterRun.current) {
      // ya vino hidratado por getServerSideProps con estos mismos filtros
      isFirstFilterRun.current = false;
      return;
    }

    setPage(1);
    fetchProductos(1, false);
    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, tematica, color, destacado, novedad, isReady]);

  // infinite scroll: un solo observer, lee el estado más reciente vía ref
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver((entries) => {
      const { page, totalPages, loadingProductos, loadingMore, fetchProductos } = latestRef.current;
      if (entries[0].isIntersecting && !loadingProductos && !loadingMore && page < totalPages) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchProductos(nextPage, true);
      }
    }, { rootMargin: '400px' });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const total = carritoMayorista.reduce((acc, item) => acc + item.cantidad, 0);
    setTotalUnidades(total);
    setShowCartButton(total >= MINIMO_UNIDADES_MAYORISTA);
    const montoTotal = carritoMayorista.reduce((acc, item) => acc + item.cantidad * item.producto.attributes.precioFinal, 0);
    setTotalPrecio(montoTotal);
  }, [carritoMayorista]);

  return {
    tipo,
    handleTipo,
    tematica,
    handleTematica,
    color,
    handleColor,
    destacado,
    handleDestacado,
    novedad,
    handleNovedad,
    handleClearFiltros,
    loadingProductos,
    loadingMore,
    productos,
    totalPages,
    totalResults,
    sentinelRef,
    topRef,
    totalUnidades,
    totalPrecio,
    showCartButton,
  };
};

export default useHook;
